import React, { useState, useEffect, useRef, memo } from 'react';
import { supabase } from '../lib/supabase_client';
import { devLog } from '../utils/devLogger';

interface PDFViewerProps {
  isOpen: boolean;
  onClose: () => void;
  orgId?: string;
  filingId?: string;
}

/**
 * PDFViewer - Native iframe-based PDF viewer (like 1.0)
 * Uses browser's built-in PDF viewer - works on ALL browsers!
 * No PDF.js, no Web Workers, no compatibility issues!
 */
const PDFViewer: React.FC<PDFViewerProps> = memo(({ isOpen, onClose, orgId, filingId }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);
  
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  // Fetch PDF from S3 via Edge Function (ONLY ONCE)
  useEffect(() => {
    const fetchPDF = async () => {
      if (!orgId || !filingId) {
        setPdfError('No filing information available');
        setLoadingPdf(false);
        return;
      }
      
      try {
        setLoadingPdf(true);
        setPdfError(null);
        devLog(`📄 Fetching PDF URL for: ${orgId}/${filingId}`);
        
        // Call Edge Function to get signed S3 URL
        const { data, error } = await supabase.functions.invoke('get-raw-upload-pdf-iframe', {
          body: {
            org_id: orgId,
            filing_id: filingId
          }
        });
        
        if (error || !data?.pdf_url) {
          setPdfError('No uploaded PDF found for this filing');
          setPdfUrl(null);
          setLoadingPdf(false);
          return;
        }
        
        // Use signed URL directly
        setPdfUrl(data.pdf_url);
        setLoadingPdf(false);
        devLog('✅ PDF URL received (valid for 1 hour)');
        
      } catch (err: any) {
        console.error('Error loading PDF:', err);
        setPdfError(err.message || 'Failed to load PDF');
        setPdfUrl(null);
        setLoadingPdf(false);
      }
    };
    
    fetchPDF();
  }, [orgId, filingId]);

  return (
    <div className="pdf-viewer">
      <div className="pdf-viewer-content">
        {pdfError ? (
          <div className="pdf-error">
            <svg className="w-16 h-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="font-semibold mb-2">PDF Not Available</p>
            <p className="text-sm text-gray-600">{pdfError}</p>
          </div>
        ) : loadingPdf ? (
          <div className="pdf-loading">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p>Loading PDF from S3...</p>
          </div>
        ) : pdfUrl ? (
          // ✅ NATIVE VIEWER - Use iframe for cross-browser compatibility
          // Browser's built-in PDF viewer, works on ALL browsers including Firefox!
          <iframe 
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ 
              border: 'none',
              display: 'block',
              minHeight: '100%'
            }}
            title="PDF Document"
          />
        ) : (
          <div className="pdf-loading">Fetching PDF...</div>
        )}
      </div>
    </div>
  );
});

export default PDFViewer;